import { useState, useEffect, useMemo, useRef } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Eye, EyeSlash, WarningCircle, ArrowRight, CaretUpDown, MagnifyingGlass, Check
} from '@phosphor-icons/react';
import { COUNTRIES, detectCountryCode, DEFAULT_SUGGESTED, flagUrl } from '../utils/countries';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ecowater-cloud/artifacts/tzhqe5c4_ECOWATER_WORDMARK_LOGO.png';

export default function LoginPage() {
  const { login, isAuthenticated, loading } = useAuth();
  const [country, setCountry] = useState(() => {
    const stored = localStorage.getItem('login_country');
    if (stored && COUNTRIES.find(c => c.code === stored)) return stored;
    return 'TZ'; // Always default to Tanzania (primary deployment country)
  });
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openCountry, setOpenCountry] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const popoverRef = useRef(null);

  const selectedCountry = useMemo(
    () => COUNTRIES.find(c => c.code === country) || COUNTRIES.find(c => c.code === 'TZ'),
    [country]
  );

  const filteredCountries = useMemo(() => {
    const q = countrySearch.trim().toLowerCase();
    if (!q) return null; // null indicates "no search → show suggested + other sections"
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.dial.includes(q) ||
      c.code.toLowerCase().includes(q)
    );
  }, [countrySearch]);

  // Suggested = the four primary markets (TZ, UG, KE, MW) in fixed order.
  const suggestedList = useMemo(
    () => DEFAULT_SUGGESTED.map(code => COUNTRIES.find(c => c.code === code)).filter(Boolean),
    []
  );

  const otherList = useMemo(() => {
    const suggestedSet = new Set(suggestedList.map(c => c.code));
    return COUNTRIES.filter(c => !suggestedSet.has(c.code));
  }, [suggestedList]);

  useEffect(() => {
    document.title = 'Ecowater Cloud';
  }, []);

  // Close country popover on outside click
  useEffect(() => {
    if (!openCountry) return;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpenCountry(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openCountry]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1b2a] flex items-center justify-center">
        <div className="animate-pulse text-white/60" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          Loading...
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Strip non-digits & remove a leading "0" (common local format)
    const localDigits = (phone || '').replace(/\D+/g, '').replace(/^0+/, '');
    if (!localDigits) { setError('Enter your phone number'); return; }
    const fullNumber = `${selectedCountry.dial}${localDigits}`;
    localStorage.setItem('login_country', country);
    setIsLoading(true);
    const result = await login(fullNumber, password);
    if (!result.success) setError(result.error);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* ====== LEFT PANEL ====== */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden bg-[#071024]">

        {/* Background image — clearly visible */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ecowater-cloud/artifacts/4085xssq__MG_9279.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }} />

        {/* Tinted gradient overlay — lets the photograph breathe while keeping
            the brand-blue mood and ensuring text is legible */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(160deg, rgba(7,16,36,0.78) 0%, rgba(10,45,94,0.55) 50%, rgba(9,27,66,0.78) 100%)',
        }} />

        {/* Bottom legibility gradient under the headline */}
        <div className="absolute inset-x-0 bottom-0 h-2/3 pointer-events-none" style={{
          background: 'linear-gradient(to top, rgba(7,16,36,0.85) 0%, rgba(7,16,36,0) 100%)',
        }} />

        {/* Grid overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(7,121,188,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(7,121,188,0.04) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />

        {/* Scan line effect */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute w-full h-[1px] bg-gradient-to-r from-transparent via-[#0A2540]/20 to-transparent"
            style={{ animation: 'scanLine 4s ease-in-out infinite', top: '0%' }} />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          {/* Logo */}
          <div style={{ animation: '0.5s ease 0.1s 1 normal both running fadeSlideUp' }}>
            <img src={LOGO_URL} alt="Ecowater" className="h-8 w-auto brightness-0 invert"
              data-testid="login-logo-desktop" />
          </div>

          {/* Hero text */}
          <div className="-mt-8">
            <p className="text-white text-sm font-semibold tracking-[0.3em] uppercase mb-4 drop-shadow-[0_1px_4px_rgba(0,0,0,0.55)]"
              style={{ fontFamily: 'JetBrains Mono, monospace', animation: '0.6s ease 0.3s 1 normal both running fadeSlideUp' }}>
              <span className="inline-block px-3 py-1 rounded-md bg-[#FFFFFF]/10 backdrop-blur-sm border border-white/15">
                Welcome to Ecowater Cloud
              </span>
            </p>
            <h1 className="text-4xl xl:text-5xl font-extrabold text-white leading-[1.15] max-w-lg drop-shadow-[0_2px_6px_rgba(0,0,0,0.45)]"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', animation: '0.6s ease 0.4s 1 normal both running fadeSlideUp' }}>
              Powering access to clean, safe and{' '}
              <span className="text-transparent bg-clip-text"
                style={{ backgroundImage: 'linear-gradient(135deg, #4dc9f6 0%, #0A2540 100%)' }}>
                sustainable water.
              </span>
            </h1>
          </div>

          {/* Spacer to keep flex justify-between layout */}
          <div />
        </div>
      </div>

      {/* ====== RIGHT PANEL ====== */}
      <div className="flex-1 flex items-center justify-center relative bg-[#fafbfd] overflow-hidden">
        {/* Subtle radial glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(7,121,188,0.08) 0%, transparent 70%)' }} />

        <div className="w-full max-w-[400px] mx-6 transition-all duration-700 opacity-100 translate-y-0">
          {/* Mobile logo (hidden on desktop) */}
          <div className="mb-8 lg:hidden flex justify-center">
            <div className="inline-flex flex-col items-end leading-none">
              <img src={LOGO_URL} alt="Ecowater" className="h-7 w-auto" />
              <span
                className="text-[12px] font-semibold tracking-[0.18em] text-[#0A2540] mt-1"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                CLOUD
              </span>
            </div>
          </div>

          {/* Sign In Card */}
          <div className="rounded-2xl border border-gray-200/80 bg-[#FFFFFF] shadow-[0_8px_40px_rgba(7,121,188,0.06)] p-8"
            data-testid="login-form-card"
            style={{ animation: '4s ease-in-out 0s infinite normal none running glowPulse' }}>

            <div className="text-center mb-7">
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Sign In
              </h2>
              <p className="text-sm text-gray-400 mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Enter your credentials to access your dashboard
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 px-3 py-2.5 rounded-lg mb-5"
                data-testid="login-error">
                <WarningCircle size={16} />
                <span className="text-xs">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  Phone Number
                </label>
                <div className="flex h-11 rounded-md border border-gray-200 bg-transparent shadow-sm focus-within:border-[#0A2540] focus-within:ring-1 focus-within:ring-[#0A2540]/20 transition-colors overflow-visible relative">
                  {/* Country selector trigger */}
                  <button
                    type="button"
                    onClick={() => setOpenCountry(v => !v)}
                    className="flex items-center gap-2 px-3 border-r border-gray-200 hover:bg-gray-50 transition-colors text-sm whitespace-nowrap"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    data-testid="country-code-trigger"
                  >
                    <img
                      src={flagUrl(selectedCountry.code)}
                      alt={selectedCountry.code}
                      className="w-5 h-[14px] object-cover rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.04)]"
                      loading="eager"
                    />
                    <span className="text-[#0A2540] font-semibold">+{selectedCountry.dial}</span>
                    <CaretUpDown size={12} className="text-gray-400" />
                  </button>

                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ''))}
                    placeholder="712 345 678"
                    className="flex-1 px-3 bg-transparent text-gray-900 text-sm placeholder:text-gray-300 focus:outline-none"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                    data-testid="phone-input"
                    required
                  />

                  {/* Country popover */}
                  {openCountry && (
                    <div
                      ref={popoverRef}
                      className="absolute left-0 top-[calc(100%+6px)] z-30 w-[360px] max-h-[420px] flex flex-col rounded-xl border border-gray-200 bg-[#FFFFFF] shadow-[0_20px_60px_-15px_rgba(7,121,188,0.25)] overflow-hidden"
                      data-testid="country-popover"
                    >
                      <div className="p-2.5 border-b border-gray-100">
                        <div className="relative">
                          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            autoFocus
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            placeholder="Search country..."
                            className="w-full h-9 pl-8 pr-2 rounded-md bg-gray-50 border border-transparent text-sm focus:outline-none focus:bg-[#FFFFFF] focus:border-[#0A2540]"
                            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                            data-testid="country-search"
                          />
                        </div>
                      </div>

                      <div className="overflow-y-auto flex-1">
                        {filteredCountries === null ? (
                          <>
                            {/* SUGGESTED */}
                            <div className="px-3 pt-3 pb-1.5">
                              <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-gray-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Suggested</p>
                            </div>
                            {suggestedList.map(c => (
                              <CountryRow
                                key={`s-${c.code}`}
                                c={c}
                                selected={country === c.code}
                                onSelect={() => { setCountry(c.code); setOpenCountry(false); setCountrySearch(''); }}
                              />
                            ))}

                            {/* OTHER */}
                            <div className="px-3 pt-3 pb-1.5 border-t border-gray-100 mt-1">
                              <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-gray-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Other countries</p>
                            </div>
                            {otherList.map(c => (
                              <CountryRow
                                key={`o-${c.code}`}
                                c={c}
                                selected={country === c.code}
                                onSelect={() => { setCountry(c.code); setOpenCountry(false); setCountrySearch(''); }}
                              />
                            ))}
                          </>
                        ) : filteredCountries.length === 0 ? (
                          <div className="p-6 text-center text-xs text-gray-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                            No countries match "{countrySearch}"
                          </div>
                        ) : (
                          filteredCountries.map(c => (
                            <CountryRow
                              key={`f-${c.code}`}
                              c={c}
                              selected={country === c.code}
                              onSelect={() => { setCountry(c.code); setOpenCountry(false); setCountrySearch(''); }}
                            />
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full h-11 px-3 pr-10 rounded-md border border-gray-200 bg-transparent text-gray-900 text-sm shadow-sm placeholder:text-gray-300 focus:outline-none focus:border-[#0A2540] focus:ring-1 focus:ring-[#0A2540]/20 transition-colors"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                    data-testid="password-input"
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    data-testid="toggle-password">
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <div className="flex justify-end pt-0.5">
                  <Link to="/forgot-password"
                    className="text-xs font-semibold text-[#0A2540] hover:underline"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                    data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 rounded-md text-white font-semibold text-sm transition-all duration-200 hover:shadow-lg hover:shadow-[#0A2540]/20 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  background: 'linear-gradient(135deg, #0A2540 0%, #0991d6 100%)'
                }}
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <>
                    <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-4 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Need help?{' '}
                <span className="text-gray-500 cursor-pointer hover:underline">Contact your administrator</span>
              </p>
            </div>
          </div>

          <p className="text-center text-[10px] text-gray-300 mt-6 tracking-wider uppercase"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            v1.0 &bull; 2026 Ecowater Cloud
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scanLine {
          0% { top: -2%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 102%; opacity: 0; }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 8px 40px rgba(7,121,188,0.06); }
          50% { box-shadow: 0 8px 40px rgba(7,121,188,0.12); }
        }
      `}</style>
    </div>
  );
}

function CountryRow({ c, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm transition-colors ${
        selected ? 'bg-[#0A2540]/8 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
      }`}
      style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
      data-testid={`country-option-${c.code}`}
    >
      <span className="flex items-center gap-2.5 truncate min-w-0">
        <span className="w-4 flex items-center justify-center shrink-0">
          {selected ? <Check size={12} weight="bold" className="text-[#0A2540]" /> : null}
        </span>
        <img
          src={`https://flagcdn.com/${c.code.toLowerCase()}.svg`}
          alt={c.code}
          className="w-5 h-[14px] object-cover rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.04)] shrink-0"
          loading="lazy"
        />
        <span className="truncate">{c.name}</span>
      </span>
      <span className="font-mono text-xs text-gray-500 shrink-0">+{c.dial}</span>
    </button>
  );
}
